import { DEFAULT_THEME_ID, getThemeById } from './themes';

/*
 * Spec 54 (rev 10 Oct) — the look-and-feel system now lives entirely in CSS:
 * one look, Clinical teal, defined as the semantic `--color-*` tokens in
 * elva.css, with every legacy and DS token mapped onto them. There is no theme
 * switching. The old runtime engine (which set ~70 tokens inline on
 * documentElement) is retired, because inline styles would override the
 * stylesheet. These exports stay so the existing imports across the six apps
 * keep compiling; `applyTheme` / `initPersistedTheme` are now no-ops.
 */

export const THEME_STORAGE_KEY = 'elva-theme';

export const THEME_CHANGE_EVENT = 'elva-theme-change';

export function notifyThemeStorageChanged(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(THEME_CHANGE_EVENT));
}

export type BgOverlay = 'none' | 'grid' | 'dots' | 'diagonal' | 'glow';

export interface ApplyThemeOptions {
  bg?: BgOverlay;
}

function resolveThemeId(id: string | null | undefined, fallback = DEFAULT_THEME_ID): string {
  const trimmed = id?.trim();
  if (trimmed && getThemeById(trimmed)) return trimmed;
  if (getThemeById(fallback)) return fallback;
  return DEFAULT_THEME_ID;
}

/** Read and validate the saved theme id; repairs stale localStorage values. */
export function resolvePersistedThemeId(
  fallback = DEFAULT_THEME_ID,
  storageKey = THEME_STORAGE_KEY,
): string {
  const resolvedFallback = resolveThemeId(fallback, DEFAULT_THEME_ID);
  if (typeof window === 'undefined') return resolvedFallback;

  try {
    const stored = localStorage.getItem(storageKey);
    const legacyStored =
      storageKey === THEME_STORAGE_KEY ? null : localStorage.getItem(THEME_STORAGE_KEY);
    const resolved = resolveThemeId(stored ?? legacyStored, resolvedFallback);
    if (stored != null && stored !== resolved) {
      localStorage.setItem(storageKey, resolved);
    }
    if (stored == null && legacyStored != null) {
      localStorage.setItem(storageKey, resolved);
    }
    return resolved;
  } catch {
    return resolvedFallback;
  }
}

export function persistThemeId(
  id: string,
  fallback = DEFAULT_THEME_ID,
  storageKey = THEME_STORAGE_KEY,
): string {
  const resolved = resolveThemeId(id, fallback);
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(storageKey, resolved);
      if (storageKey !== THEME_STORAGE_KEY) {
        localStorage.setItem(THEME_STORAGE_KEY, resolved);
      }
      notifyThemeStorageChanged();
    } catch {
      // Ignore blocked storage (private mode, etc.).
    }
  }
  return resolved;
}

/** No-op: colours come from the `--color-*` tokens in elva.css, not inline styles. */
export function applyTheme(_theme?: unknown, _options?: ApplyThemeOptions): void {
  /* intentionally empty — see the file header */
}

/** No-op: there is one look (Clinical teal); nothing to persist or apply. */
export function initPersistedTheme(_fallback = DEFAULT_THEME_ID, _storageKey = THEME_STORAGE_KEY): void {
  /* intentionally empty */
}
